import { CircularProgress } from "@material-ui/core";
import React, { useMemo } from "react";
import { Radio, Text, Field } from "components/commons";
import styles from "./input-fuel-amount.module.scss";
import locale from "localization";
import classNames from "classnames";
import { InputLitre, InputAmount } from "components/field";
import { formatAmount, parseNumber, formatVolume } from "utils";
import Big from "big.js";

const InputFuelAmount = ({
  className,
  type,
  amount,
  productCode,
  literVolume,
  modifyForm,
  setInsufficientBalance,
  fetchingWalletBalance,
  walletBalance,
  disabled,
  setExceedsLimit,
  dailyRemainingLimit,
  weeklyRemainingLimit,
  monthlyRemainingLimit,
}) => {
  const limitMessages = {
    daily: locale.yourRemainingDailyLimitIs,
    weekly: locale.yourRemainingWeeklyLimitIs,
    monthly: locale.yourRemainingMonthlyLimitIs,
  };

  const limitValues = {
    daily: dailyRemainingLimit,
    weekly: weeklyRemainingLimit,
    monthly: monthlyRemainingLimit,
  };

  const getFiniteLimitEntries = () => {
    return Object.entries(limitValues)
      .map(([period, value]) => {
        if (value == null) {
          return null;
        }

        const normalized = Number(value);
        if (!Number.isFinite(normalized)) {
          return null;
        }

        return { period, value: normalized };
      })
      .filter(Boolean);
  };

  const getLowestLimitPeriod = () => {
    const finiteLimits = getFiniteLimitEntries();

    if (!finiteLimits.length) {
      return null;
    }

    return finiteLimits.reduce((lowest, current) => {
      if (!lowest || current.value < lowest.value) {
        return current;
      }

      return lowest;
    }, null)?.period;
  };

  const remainingBuyFuelLimit = useMemo(() => {
    const limits = getFiniteLimitEntries();

    if (!limits.length) {
      return "Unlimited";
    }

    return limits.reduce((lowest, current) => {
      if (!lowest || current.value < lowest.value) {
        return current;
      }

      return lowest;
    }, null)?.value;
  }, [dailyRemainingLimit, weeklyRemainingLimit, monthlyRemainingLimit]);

  const total = useMemo(() => {
    const isInsufficient = (value) => {
      const validate = Number(parseNumber(value)) > walletBalance;
      setInsufficientBalance(validate);
      return validate;
    };

    const getExceededLimit = (liters) => {
      const period = getLowestLimitPeriod();
      if (!period) {
        if (setExceedsLimit) setExceedsLimit(false);
        return null;
      }

      const normalizedRemaining = Number(limitValues[period]);
      if (Number(liters) > normalizedRemaining) {
        if (setExceedsLimit) setExceedsLimit(true);
        return period;
      }

      if (setExceedsLimit) setExceedsLimit(false);
      return null;
    };

    const getLimitMessageProps = (period) => {
      if (!period) {
        return null;
      }

      const normalizedRemaining = Number.isFinite(Number(limitValues[period]))
        ? Number(limitValues[period])
        : 0;

      if (normalizedRemaining <= 0) {
        return {
          text: locale.yourBuyFuelLimitHasBeenReached,
          items: [],
        };
      }

      return {
        text: limitMessages[period],
        items: [<b>{formatVolume(limitValues[period])}</b>],
      };
    };

    if (type.value === "peso") {
      const t = Number(parseNumber(amount.value)) / productCode.creditPrice;
      const exceededPeriod = getExceededLimit(t);
      const limitMessageProps = getLimitMessageProps(exceededPeriod);
      const insufficient = !fetchingWalletBalance && isInsufficient(amount.value);

      return (
        <>
          {!exceededPeriod && insufficient && (
            <locale.Populate
              className={styles.error}
              text={locale.youHaveInsufficientBalance}
              items={[]}
            />
          )}
          {exceededPeriod && (
            <locale.Populate
              className={styles.error}
              text={limitMessageProps.text}
              items={limitMessageProps.items}
            />
          )}
          <locale.Populate text={locale.totalLitersIsAmount} items={[<b>{formatVolume(t)}</b>]} />
        </>
      );
    }
    const t = Number(Big(Number(parseNumber(literVolume.value))).mul(productCode.creditPrice));
    const liters = Number(parseNumber(literVolume.value));
    const exceededPeriod = getExceededLimit(liters);
    const limitMessageProps = getLimitMessageProps(exceededPeriod);
    const insufficient = !fetchingWalletBalance && isInsufficient(t);
    return (
      <>
        {!exceededPeriod && insufficient && (
          <locale.Populate
            className={styles.error}
            text={locale.youHaveInsufficientBalance}
            items={[]}
          />
        )}
        {exceededPeriod && (
          <locale.Populate
            className={styles.error}
            text={limitMessageProps.text}
            items={limitMessageProps.items}
          />
        )}
        <locale.Populate text={locale.totalAmountIs} items={[<b>{formatAmount(t)}</b>]} />
      </>
    );
  }, [
    type,
    productCode,
    amount,
    literVolume,
    walletBalance,
    setInsufficientBalance,
    fetchingWalletBalance,
    dailyRemainingLimit,
    weeklyRemainingLimit,
    monthlyRemainingLimit,
    setExceedsLimit,
  ]);

  const WalletBalance = () => {
    return fetchingWalletBalance ? (
      <CircularProgress />
    ) : (
      <>
        <Text>
          <locale.Populate
            text={locale.totalBalance}
            items={[<strong>{formatAmount(walletBalance)}</strong>]}
          />
        </Text>
        {remainingBuyFuelLimit !== null && (
          <Text>
            <locale.Populate
              text={locale.remainingBuyFuelLimit}
              items={[
                <strong>
                  {remainingBuyFuelLimit === "Unlimited"
                    ? remainingBuyFuelLimit
                    : formatVolume(remainingBuyFuelLimit)}
                </strong>,
              ]}
            />
          </Text>
        )}
      </>
    );
  };

  return (
    <div className={classNames(className, styles.container)}>
      <Radio
        className={styles.radioContainer}
        radioClassName={styles.radio}
        options={[
          { text: locale.liters, value: "liters" },
          { text: locale.peso, value: "peso" },
        ]}
        {...type}
        disabled={!productCode.value || disabled}
        onChange={(name, { value }) => {
          modifyForm({
            [name]: {
              value,
            },
            [amount.name]: {
              value: "",
              dirty: false,
              disabled: value === "liters",
            },
            [literVolume.name]: {
              value: "",
              dirty: false,
              disabled: value === "peso",
            },
          });
        }}
      />
      {type.value === "peso" ? (
        <Field {...amount}>
          <InputAmount {...amount} disabled={!productCode.value || disabled} innerLabel />
        </Field>
      ) : (
        <Field {...literVolume}>
          <InputLitre {...literVolume} disabled={!productCode.value || disabled} innerLabel />
        </Field>
      )}
      <Text className={styles.total}>{total}</Text>
      <WalletBalance />
    </div>
  );
};

export default InputFuelAmount;
